// src/components/agents/agents-page.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Plus, Play, Pencil, Trash2, Tag,
  AlertCircle, Loader2,
  Cpu, BarChart2, Clock,
  GitBranch,
} from "lucide-react";
import { Agent, agentsApi } from "@/app/services/agents/agents-api";
import { AgentBuilder } from "./agent-builder";
import { AgentRunner } from "./agent-runner";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { toast } from "sonner";

// ── Agent Card ────────────────────────────────────────────────────────────────

function AgentCard({
  agent, onRun, onEdit, onDelete,
}: {
  agent: Agent;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-4 p-4 md:p-5 rounded-2xl border transition-all hover:shadow-md"
      style={{
        backgroundColor: "var(--card)",
        borderColor: "var(--border)",
        boxShadow: "var(--elevation-sm)",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: "var(--alpha-teal-10)",
              border: "1px solid var(--alpha-teal-20)",
            }}
          >
            <GitBranch className="w-5 h-5" style={{ color: "var(--alpha-teal)" }} />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="truncate"
              style={{
                fontSize: "var(--text-base)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              {agent.name}
            </h3>
            {agent.description && (
              <p
                className="truncate"
                style={{
                  fontSize: "var(--text-xs)",
                  color: "var(--muted-foreground)",
                  marginTop: "2px",
                }}
              >
                {agent.description}
              </p>
            )}
          </div>
        </div>

        {/* Edit + Delete — always visible */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg transition-colors hover:bg-muted"
            style={{
              color: "var(--signal-blue)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
              cursor: "pointer",
            }}
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: "var(--infrared-coral)",
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted)",
              cursor: "pointer",
            }}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Prompt preview */}
      <p
        className="line-clamp-2"
        style={{
          fontSize: "var(--text-sm)",
          color: "var(--muted-foreground)",
          lineHeight: 1.6,
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {agent.prompt}
      </p>

      {/* Brand + Models */}
      <div className="space-y-2">
        {agent.brand_name && (
          <div className="flex items-center gap-2 flex-wrap">
            <span
              style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}
            >
              Brand:
            </span>
            <span
              className="px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: "var(--muted)",
                border: "1px solid var(--border)",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                maxWidth: "100%",
              }}
            >
              {agent.brand_name}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Cpu
            className="w-3.5 h-3.5 flex-shrink-0"
            style={{ color: "var(--muted-foreground)" }}
          />
          {agent.selected_models.map((m) => (
            <span
              key={m}
              className="px-2 py-0.5 rounded-full text-xs capitalize"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                color: "var(--alpha-teal)",
                border: "1px solid var(--alpha-teal-20)",
              }}
            >
              {m}
            </span>
          ))}
        </div>
        {agent.tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Tag
              className="w-3 h-3 flex-shrink-0"
              style={{ color: "var(--muted-foreground)" }}
            />
            {agent.tags.map((t) => (
              <span
                key={t}
                className="px-2 py-0.5 rounded-full text-xs"
                style={{
                  backgroundColor: "var(--muted)",
                  color: "var(--muted-foreground)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Run options */}
      <div className="flex flex-wrap gap-2">
        {agent.run_options.perception_score && (
          <span
            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs"
            style={{
              backgroundColor: "var(--muted)",
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
            }}
          >
            <BarChart2 className="w-3 h-3" /> Score
          </span>
        )}
        {agent.run_options.enable_download && (
          <span
            className="px-2 py-1 rounded-full text-xs"
            style={{
              backgroundColor: "var(--muted)",
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
            }}
          >
            ↓ Download
          </span>
        )}
        {agent.run_options.enable_email && (
          <span
            className="px-2 py-1 rounded-full text-xs"
            style={{
              backgroundColor: "var(--muted)",
              color: "var(--muted-foreground)",
              border: "1px solid var(--border)",
            }}
          >
            ✉ Email
          </span>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between pt-3 mt-auto border-t gap-3"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          className="flex items-center gap-1 text-xs"
          style={{ color: "var(--muted-foreground)" }}
        >
          <Clock className="w-3 h-3 flex-shrink-0" />
          {new Date(agent.created_at).toLocaleDateString()}
        </div>
        <button
          onClick={onRun}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90 flex-shrink-0"
          style={{
            backgroundColor: "var(--alpha-teal)",
            color: "white",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Play className="w-3.5 h-3.5" />
          Run
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

interface AgentsPageProps {
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (c: boolean) => void;
  activeBrandName?: string;
}

export function AgentsPage({
  isSidebarCollapsed: _isSidebarCollapsed,
  onSidebarToggle: _onSidebarToggle,
  activeBrandName,
}: AgentsPageProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [builderAgent, setBuilderAgent] = useState<Agent | null | undefined>(undefined);
  const [runningAgent, setRunningAgent] = useState<Agent | null>(null);
  const [workflowPendingDelete, setWorkflowPendingDelete] = useState<Agent | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAgents(await agentsApi.list({ brandName: activeBrandName }));
    } catch (e: any) {
      setError(e.message || "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, [activeBrandName]);

  useEffect(() => { load(); }, [load]);

  const requestDelete = (agent: Agent) => setWorkflowPendingDelete(agent);

  const confirmDelete = async () => {
    const agent = workflowPendingDelete;
    if (!agent) return;
    try {
      await agentsApi.delete(agent.id);
      setAgents((prev) => prev.filter((a) => a.id !== agent.id));
      toast.success("Workflow deleted");
    } catch (e: any) {
      toast.error(e.message || "Failed to delete workflow");
    } finally {
      setWorkflowPendingDelete(null);
    }
  };

  const handleSaved = (_saved: Agent) => {
    const wasEditing = builderAgent !== null;
    setBuilderAgent(undefined);
    load();
    toast.success(wasEditing ? "Workflow updated" : "Workflow created");
  };

  // ── Full-page builder mode ─────────────────────────────────────────────────
  if (builderAgent !== undefined) {
    return (
      <AgentBuilder
        agent={builderAgent}
        onClose={() => setBuilderAgent(undefined)}
        onSaved={handleSaved}
      />
    );
  }

  // ── List view ─────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <div className="flex-1 min-w-0 overflow-auto">

        {/* Header */}
        <div
          className="border-b px-4 md:px-8 py-6 md:py-8"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--background)",
          }}
        >
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div
              className="flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                border: "1px solid var(--alpha-teal-20)",
              }}
            >
              <GitBranch className="w-5 h-5" style={{ color: "var(--alpha-teal)" }} />
            </div>
            <span
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--muted-foreground)",
                fontWeight: 600,
              }}
            >
              Workflows
            </span>
          </div>

          {/* Title + description + New Workflow button — stacks on mobile */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6">
            <div className="min-w-0 flex-1">
              <h1
                style={{
                  fontSize: "clamp(var(--text-xl), 4.5vw, var(--text-2xl))",
                  fontWeight: "var(--font-weight-semibold)",
                  lineHeight: 1.3,
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                AI Workflows
              </h1>
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: "var(--text-sm)",
                  lineHeight: 1.5,
                  marginTop: "8px",
                  maxWidth: "600px",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {activeBrandName ? (
                  <>
                    Visual workflows that run custom prompts against multiple AI models
                    for <strong>{activeBrandName}</strong>.
                  </>
                ) : (
                  <>
                    Build visual workflows that run custom prompts against multiple AI models,
                    generate perception scores, and export results.
                  </>
                )}
              </p>
            </div>
            <button
              onClick={() => setBuilderAgent(null)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl flex-shrink-0 transition-all hover:opacity-90"
              style={{
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-semibold)",
                backgroundColor: "var(--alpha-teal)",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              <Plus className="w-4 h-4" />
              New Workflow
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 md:px-8 py-6 md:py-8">

          {loading && (
            <div className="flex items-center justify-center py-12 md:py-16">
              <Loader2
                className="w-7 h-7 animate-spin"
                style={{ color: "var(--alpha-teal)" }}
              />
            </div>
          )}

          {error && !loading && (
            <div
              className="flex items-start gap-3 p-4 rounded-xl mb-5"
              style={{
                backgroundColor: "var(--error-bg)",
                border: "1px solid var(--error-border)",
              }}
            >
              <AlertCircle
                className="w-5 h-5 flex-shrink-0 mt-0.5"
                style={{ color: "var(--error-text)" }}
              />
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--error-text)",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {error}
              </p>
            </div>
          )}

          {!loading && !error && agents.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-12 md:py-16 px-4 rounded-2xl border-2 border-dashed text-center"
              style={{ borderColor: "var(--border)" }}
            >
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  backgroundColor: "var(--alpha-teal-10)",
                  border: "1px solid var(--alpha-teal-20)",
                }}
              >
                <GitBranch className="w-8 h-8" style={{ color: "var(--alpha-teal)" }} />
              </div>
              <h3
                style={{
                  fontSize: "var(--text-lg)",
                  fontWeight: 600,
                  marginBottom: "8px",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {activeBrandName ? `No workflows for ${activeBrandName}` : "No workflows yet"}
              </h3>
              <p
                style={{
                  fontSize: "var(--text-sm)",
                  color: "var(--muted-foreground)",
                  marginBottom: "20px",
                  maxWidth: "360px",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                }}
              >
                {activeBrandName
                  ? `Create your first workflow for ${activeBrandName} using the visual flow editor.`
                  : "Build your first workflow with the visual flow editor."}
              </p>
              <button
                onClick={() => setBuilderAgent(null)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl"
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  backgroundColor: "var(--alpha-teal)",
                  color: "white",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Plus className="w-4 h-4" />
                Create First Workflow
              </button>
            </div>
          )}

          {!loading && !error && agents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
              {agents.map((a) => (
                <AgentCard
                  key={a.id}
                  agent={a}
                  onRun={() => setRunningAgent(a)}
                  onEdit={() => setBuilderAgent(a)}
                  onDelete={() => requestDelete(a)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Runner modal */}
      {runningAgent && (
        <AgentRunner
          agent={runningAgent}
          onClose={() => setRunningAgent(null)}
          onRunSaved={() => {}}
        />
      )}

      {/* Delete-workflow confirmation */}
      <ConfirmDialog
        open={workflowPendingDelete !== null}
        onOpenChange={(open) => { if (!open) setWorkflowPendingDelete(null); }}
        onConfirm={confirmDelete}
        title={workflowPendingDelete ? `Delete "${workflowPendingDelete.name}"?` : "Delete workflow?"}
        description="This will permanently delete the workflow. Past run results in Workflow History will be preserved, but the workflow definition itself cannot be recovered."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}